/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import VehiclesService from 'api/vehicles/VehiclesService';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './VehicleScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import DevicesService from 'api/devices/DevicesService';
import DriversService from 'api/drivers/DriversService';

type Props = {
};

const VehicleScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVehiclesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_VEHICLES_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<VehicleDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<VehicleDto>();
    const [devices, setDevices] = useState<SelectValueLabel[]>([]);
    const [drivers, setDrivers] = useState<SelectValueLabel[]>([]);
    const [refresh, setRefresh] = useState<number>(1);
    const [formData, setFormData] = useState({
        id: '',
        licensePlate: '',
        designation: '',
        fuelTank: 0,
        companyId: '',
        driverId: '',
        deviceId: '',
        rowVersion: ''
    });

    const getData = async () => {
        try {
            let result: any = { id: '', licensePlate: '', companyId: '' };
            Loading.show();
            const devices = await DevicesService.getMyDevices();
            setDevices(devices);
            const drivers = await DriversService.getDrivers();
            setDrivers(drivers);
            if (id) {
                result = await VehiclesService.getById(id);
            }
            setItem(result);
            setFormData(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get vehicle information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setRefresh(refresh + 1);
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/vehicle/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/vehicles');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }

        try {
            await VehiclesService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete vehicle', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: VehicleDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: VehicleDto = {
                id: formData.id,
                fuelTank: formData.fuelTank,
                licensePlate: formData.licensePlate,
                designation: formData.designation,
                deviceId: formData.deviceId,
                driverId: formData.driverId,
                rowVersion: formData.rowVersion
            };
            if (model && model.id) {
                await VehiclesService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await VehiclesService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toast(t('messages.vehicle_already_exists'));
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the vehicle with id: ${form.id}`, error);
                toast.error(t('messages.error_load_info'));
            }
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('vehicles.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('vehicles.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <Form onSubmit={onSubmit} noValidate validated={validated} key={refresh}>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="licensePlate">
                            <Form.Label>{t('vehicles.license_plate')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="licensePlate" type="text" defaultValue={item?.licensePlate} readOnly={isDetails} onChange={handleChange} placeholder={t('vehicles.license_plate')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="designation">
                            <Form.Label>{t('vehicles.designation')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="designation" type="text" defaultValue={item?.designation} readOnly={isDetails} onChange={handleChange} placeholder={t('vehicles.designation')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="fuelTank">
                            <Form.Label>{t('vehicles.fuel_tank')}(l){!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="fuelTank" type="number" defaultValue={item?.fuelTank} readOnly={isDetails} onChange={handleChange} placeholder={t('vehicles.fuel_tank')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="driverId">
                            <Form.Label>{t('vehicles.driver')}</Form.Label>
                            <Form.Select name="driverId" defaultValue={item?.driverId} disabled={isDetails}
                                onChange={(e: any) => { handleChange(e); }}>
                                <option value={''}>{t('common.select_option')}</option>
                                { drivers &&
                                    drivers.map(c => <option key={c.value} value={c.value}>{c.label}</option>)
                                }
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="deviceId">
                            <Form.Label>{t('vehicles.device')}</Form.Label>
                            <Form.Select name="deviceId" defaultValue={item?.deviceId} disabled={isDetails}
                                onChange={(e: any) => { handleChange(e); }}>
                                <option value={''}>{t('common.select_option')}</option>
                                { devices &&
                                    devices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)
                                }
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasVehiclesWritePolicy &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as VehicleDto)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && hasVehiclesWritePolicy &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => navigateTo('edit', item?.id) }>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && hasVehiclesWritePolicy &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.licensePlate ?? '' })} />
        </ScreenTitle>
    );
};

export default VehicleScreen;
